import inRange from 'lodash/inRange';
import isNumber from 'lodash/isNumber';
import { useMemo } from 'react';

import { CommandCenterHousehold } from '@ndvr/customer-api';

import { filterByDateRange, toNumber } from 'utils';

import { HouseholdFilters, useHouseholdFiltersContext } from './HouseholdFiltersContext';

const filterByPrimaryAdvisors = (household: CommandCenterHousehold, filters: HouseholdFilters) =>
    household.primaryAdvisorGUID != null && filters.primaryAdvisorGUIDs.includes(household.primaryAdvisorGUID);

const filterByAUMRange = (household: CommandCenterHousehold, filters: HouseholdFilters) => {
    const aum = toNumber(household.aum);
    const minAUM = filters.minAUM;
    const maxAUM = filters.maxAUM;
    const isMinAUM = isNumber(minAUM);
    const isMaxAUM = isNumber(maxAUM);

    if (!isNumber(aum)) {
        return false;
    }
    if (isMinAUM && isMaxAUM) {
        return inRange(aum, minAUM, maxAUM);
    }
    if (isMinAUM) {
        return minAUM <= aum;
    }
    if (isMaxAUM) {
        return aum <= maxAUM;
    }
    return isNumber(aum);
};

export const AFFILIATION_LEVEL_EXTERNAL_VALUE = 'external';

export const filterByAffiliationLevel = (
    household: Pick<CommandCenterHousehold, 'ffAffiliationLevel'>,
    filters: Pick<HouseholdFilters, 'affiliationLevels'>,
) => {
    if (filters.affiliationLevels.length === 0) return true;
    if (filters.affiliationLevels.includes(AFFILIATION_LEVEL_EXTERNAL_VALUE) && !household.ffAffiliationLevel) {
        return true;
    }
    return filters.affiliationLevels.includes(household.ffAffiliationLevel!);
};

const filterByCreatedAt = (household: CommandCenterHousehold, filters: HouseholdFilters): boolean => {
    return filterByDateRange(household.createdAt, filters.createdAtStart, filters.createdAtEnd);
};

const filterByAgreementSigned = (household: CommandCenterHousehold, filters: HouseholdFilters): boolean => {
    return filterByDateRange(
        household.startDate || 'invalid',
        filters.agreementSignedStart,
        filters.agreementSignedEnd,
    );
};

export const useHouseholdFilters = (households: CommandCenterHousehold[]) => {
    const {
        filters,
        isAdvisorFilterActive,
        isAUMRangeFilterActive,
        isAffiliationLevelFilterActive,
        isCreatedAtFilterActive,
        isAgreementSignedFilterActive,
    } = useHouseholdFiltersContext();

    const activeFilters = useMemo(
        () =>
            [
                {
                    isActive: isAdvisorFilterActive,
                    filterFunction: (household: CommandCenterHousehold) => filterByPrimaryAdvisors(household, filters),
                },
                {
                    isActive: isAUMRangeFilterActive,
                    filterFunction: (household: CommandCenterHousehold) => filterByAUMRange(household, filters),
                },
                {
                    isActive: isAffiliationLevelFilterActive,
                    filterFunction: (household: CommandCenterHousehold) => filterByAffiliationLevel(household, filters),
                },
                {
                    isActive: isCreatedAtFilterActive,
                    filterFunction: (household: CommandCenterHousehold) => filterByCreatedAt(household, filters),
                },
                {
                    isActive: isAgreementSignedFilterActive,
                    filterFunction: (household: CommandCenterHousehold) => filterByAgreementSigned(household, filters),
                },
            ].filter(({ isActive }) => isActive),
        [
            filters,
            isAdvisorFilterActive,
            isAUMRangeFilterActive,
            isAffiliationLevelFilterActive,
            isCreatedAtFilterActive,
            isAgreementSignedFilterActive,
        ],
    );

    const filteredHouseholds = useMemo(
        () => households.filter((household) => activeFilters.every(({ filterFunction }) => filterFunction(household))),
        [households, activeFilters],
    );

    return {
        data: filteredHouseholds,
    };
};
